import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import {
    ClockIcon,
    MapPinIcon,
    CalendarDaysIcon,
    PhoneIcon,
    ArrowLeftIcon,
    InformationCircleIcon,
    GlobeAltIcon
} from '@heroicons/react/24/outline';
import Navbar from './Navbar';
import Footer from './Footer';
import NoImg from '../assets/images/no-image-icon-23485.png';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import useBreadcrumbStore from '../SharedComponents/breadcrumbStore'; // مسیر را بررسی کنید

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- Interfaces ---
interface PlaceImage {
    image_id: string;
    file_url: string;
    filename: string;
}

interface PlaceDetail {
    place_id: string;
    name: string;
    address: string;
    phone: string;
    website: string;
    instagram: string;
    latitude: number;
    longitude: number;
    price_range: string | null;
    rate: number | null;
    description: string;
    sub_category: string;
    opening_hours: string | null;
    images: PlaceImage[];
    cities: { id: number; name: string }[];
    place_tags: { id: number; name: string }[];
    categories: { id: number; name: string }[];
    avg_rating: number | null;
    review_count: number;
}

// ✅ اینترفیس پاسخ API
interface PlaceApiResponse {
    success: boolean;
    data: PlaceDetail;
    message: string;
}

interface PlanStep {
    id: number;
    order: number;
    place: string;
    place_name: string;
    activity: string;
    start_time: string;
    duration_minutes: number;
    suggestion: string;
}

interface Plan {
    id: number;
    name: string;
    description: string;
    suggested_season: string;
    suggested_months: string;
    created_at: string;
    steps: PlanStep[];
    interests: unknown[];
}

interface PlanApiResponse {
    success: boolean;
    data: Plan;
    message: string;
}

const StarRating = ({ rating }: { rating: number }) => {
    return (
        <div className="flex justify-between items-center">
            <div className="flex items-center">
                <StarIconSolid className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            </div>
        </div>
    );
};

// --- Fetch Functions ---
const fetchPlanById = async (id: string | undefined): Promise<Plan> => {
    if (!id) throw new Error("شناسه برنامه یافت نشد");
    const res = await fetch(`${BASE_URL}/api/plans/${id}/`);
    if (!res.ok) throw new Error("خطا در دریافت جزئیات برنامه");
    const json: PlanApiResponse = await res.json();
    return json.data;
};

const fetchPlaceDetails = async (placeId: string): Promise<PlaceDetail> => {
    const res = await fetch(`${BASE_URL}/api/places/${placeId}/`);
    if (!res.ok) throw new Error("خطا در دریافت جزئیات مقصد");
    const json: PlaceApiResponse = await res.json();
    
    // ✅ اصلاح: json.data را برگردان نه کل json
    return json.data;
};

// --- Helper Components ---
const PlaceCard: React.FC<{ step: PlanStep }> = ({ step }) => {
    const { data: placeData, isLoading: isPlaceLoading } = useQuery({
        queryKey: ['place', step.place],
        queryFn: () => fetchPlaceDetails(step.place),
        enabled: !!step.place,
    });

    const formatTime = (timeString: string) => {
        if (!timeString) return '';
        return timeString.substring(0, 5);
    };

    // Extract the first image URL from the images array
    const displayImage = placeData?.images && placeData.images.length > 0
        ? `${BASE_URL}${placeData.images[0].file_url}`
        : NoImg;

    return (
        <Link to={`/places/${step.place}`} className="block relative flex gap-6 group font-myYekanFaNumRegular">
            {/* Timeline Icon */}
            <div className="hidden md:flex flex-col items-center z-10 w-2 pt-4 pointer-events-none">
                <div className="w-8 h-8 rounded-full bg-green-100 border-4 border-white shadow-sm flex items-center justify-center text-green-600 font-bold text-sm transition-transform group-hover:scale-110">
                    {step.order}
                </div>
                <div className="flex-1 w-0.5 bg-gray-200 my-1 min-h-[20px]"></div>
            </div>

            {/* Card Content */}
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-all duration-300 flex flex-col md:flex-row gap-5 cursor-pointer">
                {/* Image Section */}
                <div className="w-full md:w-48 h-48 shrink-0 bg-gray-100 rounded-lg overflow-hidden relative">
                    <img
                        src={displayImage}
                        alt={placeData?.name || step.place_name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = NoImg;
                        }}
                    />
                    {/* Activity Badge */}
                    <div className="absolute top-2 right-2 pointer-events-none">
                        <span className="bg-white/90 backdrop-blur-sm text-gray-800 text-xs px-2 py-1 rounded-md font-bold shadow-sm border border-gray-200">
                            {step.activity}
                        </span>
                    </div>
                </div>

                {/* Details Section */}
                <div className="flex-1 flex flex-col">
                    {/* Header: Title & Time */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-2">
                        <div className="flex-1 min-w-0 w-full">
                            <h3 className="flex flex-row sm:flex-row sm:items-center gap-1 text-lg font-myYekanFaNumDemiBold text-gray-800 mb-1 leading-tight">
                                <span className="whitespace-nowrap">{placeData?.name || step.place_name}</span>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600">{placeData?.sub_category}</span>
                                </div>
                            </h3>
                        </div>
                        <div className="flex flex-row items-center gap-1 pointer-events-none w-full md:w-auto justify-start md:justify-end">
                            <div className="flex items-center gap-1 text-sm font-bold text-gray-700 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                                <ClockIcon className="h-4 w-4 text-green-600" />
                                <span className="font-myYekanFaNum">{formatTime(step.start_time)}</span>
                            </div>
                            <span className="text-xs text-gray-400 font-myYekanFaNum">{step.duration_minutes} دقیقه</span>
                        </div>
                    </div>

                    {/* Rating & Price Row */}
                    <div className="flex items-center mb-3 pointer-events-none">
                        {placeData?.avg_rating && placeData.avg_rating > 0 && (
                            <div className="flex items-center gap-1 bg-green-50 px-1 py-1 rounded-lg border border-green-100">
                                <span className="text-sm font-bold">{placeData.avg_rating}</span>
                                <StarRating rating={placeData.avg_rating} />
                                {placeData.review_count > 0 && (
                                    <span className="text-xs text-gray-500">({placeData.review_count})</span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    {placeData?.description && (
                        <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-3 text-justify pointer-events-none">
                            <ReactMarkdown>{placeData.description}</ReactMarkdown>
                        </p>
                    )}

                    {/* Contact & Address Details */}
                    <div className="mt-auto space-y-2 text-sm text-gray-600 border-t border-gray-100 pt-3 pointer-events-none">
                        {placeData?.address && (
                            <div className="flex items-start gap-2">
                                <MapPinIcon className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                                <span className="line-clamp-1">{placeData.address}</span>
                            </div>
                        )}
                        <div className="flex items-center gap-4 flex-wrap">
                            {placeData?.phone && (
                                <div className="flex items-center gap-2">
                                    <PhoneIcon className="h-4 w-4 text-gray-400" />
                                    <span dir="ltr" className="font-myYekanFaNum">{placeData.phone}</span>
                                </div>
                            )}
                            {placeData?.website && (
                                <a
                                    href={placeData.website}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1 text-blue-600 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <GlobeAltIcon className="h-4 w-4" />
                                    <span>وب‌سایت</span>
                                </a>
                            )}
                            {placeData?.instagram && (
                                <a
                                    href={placeData.instagram}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1 text-pink-600 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <span>اینستاگرام</span>
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Suggestion from Plan */}
                    {step.suggestion && (
                        <div className="mt-3 bg-indigo-50 border border-indigo-100 rounded-lg p-3 flex gap-3 pointer-events-none">
                            <InformationCircleIcon className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
                            <div>
                                <span className="block text-xs font-bold text-indigo-700 mb-1">پیشنهاد ویژه: <span className="text-sm text-indigo-800">{step.suggestion}</span>
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
};

// --- Main Component ---
const PlanDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { setBreadcrumbData, clearBreadcrumbData } = useBreadcrumbStore();

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['plan', id],
        queryFn: () => fetchPlanById(id),
        enabled: !!id,
        staleTime: 10 * 60 * 1000,
    });

    // ✅ تنظیم breadcrumb بر اساس داده‌های دریافتی
    useEffect(() => {
        if (data && !isLoading) {
            // ست کردن نام برنامه در استور
            setBreadcrumbData(null, null, data.name, false);
        } else if (isLoading) {
            // در حال لودینگ - می‌توانیم لودینگ را true کنیم
            setBreadcrumbData(null, null, null, true);
        }
    }, [data, isLoading, setBreadcrumbData]);

    // ✅ پاک کردن breadcrumb هنگام خروج از صفحه
    useEffect(() => {
        return () => {
            clearBreadcrumbData();
        };
    }, [clearBreadcrumbData]);

    return (
        <div className="min-h-screen bg-white">
            <Navbar />
            <div className="w-full desktop:px-[200px] px-[16px] py-6">

                {isLoading ? (
                    <div className="flex justify-center items-center py-40">
                        <div className="text-gray-500 text-lg font-myYekanFaNumRegular">در حال بارگذاری جزئیات برنامه...</div>
                    </div>
                ) : isError ? (
                    <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg text-center">
                        {(error as Error).message}
                    </div>
                ) : data ? (
                    <div className="">
                        {/* Plan Info Header */}
                        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 mb-8 border border-gray-100">
                            <h1 className="text-2xl font-myYekanFaNumDemiBold text-gray-800 mb-4">
                                {data.name}
                            </h1>
                            {data.description && (
                                <p className="text-gray-600 mb-6 leading-relaxed text-justify whitespace-pre-line font-myYekanFaNumRegular">
                                    {data.description}
                                </p>
                            )}
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                {data.suggested_season && (
                                    <div className="flex items-center bg-gray-50 px-3 py-1.5 rounded-full">
                                        <CalendarDaysIcon className="h-5 w-5 ml-2 text-green-600" />
                                        <span className="font-myYekanFaNumDemiBold"> فصل پیشنهادی:</span>
                                        <span className="mr-1 font-myYekanFaNumRegular">{data.suggested_season}</span>
                                    </div>
                                )}
                                {data.suggested_months && (
                                    <div className="flex items-center bg-gray-50 px-3 py-1.5 rounded-full">
                                        <ClockIcon className="h-5 w-5 ml-2 text-green-600" />
                                        <span className="font-myYekanFaNumDemiBold">ماه پیشنهادی:</span>
                                        <span className="mr-1 font-myYekanFaNumRegular">{data.suggested_months}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Timeline / Steps */}
                        <div className="relative pb-10">
                            <div className="space-y-6">
                                {data.steps.map((step) => (
                                    <PlaceCard key={step.id} step={step} />
                                ))}
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
            <Footer />
        </div>
    );
};

export default PlanDetails;